import { useDispatch, useSelector } from 'react-redux';
import * as React from 'react';

import { fetchFavoriteEntities } from '../../actions/favoriteEntities';
import { setDealType, setFavoriteEntityType } from '../../actions/filters';
import { unselectAll } from '../../actions/selected';
import { Filters, trackCategoryClick, trackSavedSearchClick } from '../../components/Filters';
import { EEntityType, ETabType } from '../../types/filters';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { addQueryToHistory } from '../../hooks/browserHistory';
import { getSavedSearches } from '../../thunk/savedSearches/getSavedSearches';
import { useFavoritesUxfeedbackCampaign } from '../../hooks/uxfeedback';
import { EGetFavoriteEntitiesStatus } from '../../types/results';
import { selectActiveTab } from '../../selectors/activeTab';
import { initializeActiveTabFromUrl, setActiveTab } from '../../actions/activeTab';
import { isAbUseExperimentGroup, selectExperiments } from '../../selectors/experiments';
import { PERS_SAVED_SEARCH_FAVORITES_MERGE_EXPERIMENT } from '../../constants/experiments';

export const FiltersContainer = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const { sendFavoritesUxfeedbackCampaign } = useFavoritesUxfeedbackCampaign();

  const results = useSelector((state: IApplicationState) => state.results);
  const { activeTab } = useSelector(selectActiveTab);
  const experiments = useSelector(selectExperiments);
  const isExperimentEnabled = isAbUseExperimentGroup(experiments, PERS_SAVED_SEARCH_FAVORITES_MERGE_EXPERIMENT, 'B');

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabFromURL = params.get('activeTab') as ETabType | null;

    if (isExperimentEnabled && tabFromURL) {
      dispatch(initializeActiveTabFromUrl({ activeTab: tabFromURL }));
    } else {
      const defaultTab = ETabType.Аdvertisements;
      dispatch(initializeActiveTabFromUrl({ activeTab: defaultTab }));
      addQueryToHistory({ activeTab: defaultTab });
    }

    // Слушаем изменения URL (кнопки назад/вперед)
    const handlePopState = () => {
      const newParams = new URLSearchParams(window.location.search);
      const newTab = newParams.get('activeTab') as ETabType | null;
      dispatch(initializeActiveTabFromUrl({ activeTab: newTab }));
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dispatch]);

  const handleCategoryClick = React.useCallback(
    (category: EEntityType | null) => {
      dispatch(unselectAll());
      dispatch(setFavoriteEntityType(category));
      dispatch(setDealType(null));
      dispatch(fetchFavoriteEntities({ offset: 0 }));
      trackCategoryClick(category);
      sendFavoritesUxfeedbackCampaign();
    },
    [dispatch],
  );

  //обработчик клика по табу "Объявления"
  const handleAdvertisementsClick = React.useCallback(() => {
    addQueryToHistory({ activeTab: ETabType.Аdvertisements });
    dispatch(setActiveTab({ activeTab: ETabType.Аdvertisements, isInitialized: true }));
    handleCategoryClick(null);
  }, [handleCategoryClick]);

  //обработчик клика по табу "ЖК"
  const handleJKClick = React.useCallback(() => {
    addQueryToHistory({ activeTab: ETabType.JK });
    dispatch(setActiveTab({ activeTab: ETabType.JK, isInitialized: true }));
    handleCategoryClick(EEntityType.Newbuilding);
  }, [handleCategoryClick]);

  //обработчик клика по табу "Поиски"
  const handleSavedSearchClick = React.useCallback(() => {
    addQueryToHistory({ activeTab: ETabType.SavedSearches });
    dispatch(setActiveTab({ activeTab: ETabType.SavedSearches, isInitialized: true }));

    trackSavedSearchClick();
    dispatch(getSavedSearches());
  }, [dispatch]);

  if (results.status === EGetFavoriteEntitiesStatus.Failed) {
    return null;
  }

  return (
    <Filters
      type={activeTab}
      onAdvertisementsClick={handleAdvertisementsClick}
      onJKClick={handleJKClick}
      onSavedSearchClick={handleSavedSearchClick}
    />
  );
};
